import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import {
  VILLA_NADIA_HERO_IMAGE,
  VILLA_NADIA_IMAGES,
} from '../data/villaNadiaImages';

const VillaNadiaScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const amenities = t('villas.nadia.amenities', { returnObjects: true }) || [
    'Air conditioning throughout',
    '4 bedrooms with private bathrooms (shower & bathtub)',
    'Additional separate bathroom',
    'Spacious European-style living room with fireplace',
    'Fully equipped kitchen with gas stove and dishwasher',
    'Beautiful large garden',
    'Heated pool (15 x 5 m)',
    'Sun loungers with umbrellas',
    'Lounge area',
    'Terrace with dining table',
    'Boccia court',
    'Parking space',
    'Bath and shower towels',
    'Washing machine and dryer',
    'WiFi throughout the villa',
    'Solar panels',
  ];

  const galleryImages = VILLA_NADIA_IMAGES;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('${VILLA_NADIA_HERO_IMAGE}')`,
        }}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-sofia font-bold mb-4">
            {t('villas.nadia.title')}
          </h1>
          <p className="text-xl font-sofia font-light mb-2">
            {t('villas.nadia.subtitle')}
          </p>
          <div className="inline-block bg-[#81736d] text-white px-4 py-2 rounded-full text-sm font-sofia font-medium">
            {t('villas.nadia.comingSoonBadge')}
          </div>
        </div>
      </section>

      {/* Villa Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-sofia font-bold text-black mb-6">
                {t('villas.nadia.aboutTitle')}
              </h2>
              <p className="text-md font-sofia text-[#81736d] mb-2">
                {t('villas.nadia.pricing')}
              </p>
              <p className="text-sm font-sofia text-text-light mb-6">
                {t('villas.nadia.availableDate')}
              </p>
              <p className="text-lg font-sofia text-text-secondary mb-6">
                {t('villas.nadia.aboutDescription1')}
              </p>
              <p className="text-lg font-sofia text-text-secondary mb-8">
                {t('villas.nadia.aboutDescription2')}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">450m²</div>
                  <div className="font-sofia text-black">{t('gallery.stats.livingSpace')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">4</div>
                  <div className="font-sofia text-black">{t('villas.nadia.bedrooms')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">15x5m</div>
                  <div className="font-sofia text-black">{t('villas.nadia.pool')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">8</div>
                  <div className="font-sofia text-black">{t('villas.nadia.capacity')}</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="w-full md:w-auto"
                  disabled
                >
                  {t('villas.nadia.comingSoon')}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full md:w-auto"
                  onClick={() => navigate('/our-villas')}
                >
                  {t('ourVillas.viewAllVillas')}
                </Button>
              </div>
            </div>

            <div>
              <img
                src={VILLA_NADIA_IMAGES[1]}
                alt={t('villas.nadia.title')}
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-6"
              />
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={VILLA_NADIA_IMAGES[2]}
                  alt={`${t('villas.nadia.title')} interior`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <img
                  src={VILLA_NADIA_IMAGES[3]}
                  alt={`${t('villas.nadia.title')} pool`}
                  className="w-full h-32 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-16" style={{ background: 'url("/assets/icons/top-img-bg.png")' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-sofia font-bold text-black text-center mb-12">
            {t('villas.nadia.amenitiesTitle')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                <div className="w-6 h-6 bg-[#81736d] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="font-sofia text-text-primary">{amenity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-sofia font-bold text-black text-center mb-12">
            {t('villas.nadia.galleryTitle')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative group cursor-pointer">
                <img
                  src={image}
                  alt={`${t('villas.nadia.title')} ${index + 1}`}
                  className="w-full h-64 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-300 rounded-lg"></div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" onClick={() => navigate('/our-villas')}>
              {t('ourVillas.viewAllVillas')}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('${VILLA_NADIA_IMAGES[4]}')`,
          }}
        ></div>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-sofia font-bold mb-4">
            {t('villas.nadia.ctaTitle')}
          </h2>
          <p className="text-lg font-sofia font-light mb-8">
            {t('villas.nadia.ctaDescription')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              onClick={() => navigate('/contact')}
            >
              {t('common.contactUs')}
            </Button>
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => navigate('/our-villas')}
            >
              {t('ourVillas.viewAllVillas')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VillaNadiaScreen;
